import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "title", "status", "saveButton"]
  static values = {
    autosaveInterval: { type: Number, default: 2000 },
    autoDraft: Boolean,
    discardUrl: String,
    touchUrl: String,
    modifiedAt: String
  }

  connect() {
    this.saveTimeout = null
    this.isSaving = false
    this.hasEverChanged = false
    this.touchedPersisted = false
    this.modifiedAt = this.modifiedAtValue ? new Date(this.modifiedAtValue) : null
    this.lastSavedTitle = this.hasTitleTarget ? this.titleTarget.value : ""
    this.lastSavedContent = this.hasEditorTarget ? this.editorTarget.value : ""
    this.updateStatus(this.modifiedStatus())

    this.trixEditor = this.element.querySelector("trix-editor")
    this.handleTrixChange = () => this.scheduleSave()
    this.trixEditor?.addEventListener("trix-change", this.handleTrixChange)

    this.handlePageHide = () => this.discardUntouchedDraft()
    window.addEventListener("pagehide", this.handlePageHide)
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    if (this.hasUnsavedChanges()) {
      this.save()
    }

    this.trixEditor?.removeEventListener("trix-change", this.handleTrixChange)
    window.removeEventListener("pagehide", this.handlePageHide)
  }

  scheduleSave() {
    this.noteUserInteraction()
    this.updateStatus("Saving...")

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    this.saveTimeout = setTimeout(() => {
      this.save()
    }, this.autosaveIntervalValue)
  }

  async handleSubmit(event) {
    event.preventDefault()
    await this.save()
  }

  async save() {
    if (this.isSaving || !this.hasUnsavedChanges()) return

    this.isSaving = true
    this.updateStatus("Saving...")
    const form = this.element.closest("form")
    const formData = new FormData(form)

    try {
      const response = await fetch(form.action, {
        method: "PATCH",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const payload = await response.json()
        this.lastSavedTitle = this.hasTitleTarget ? this.titleTarget.value : ""
        this.lastSavedContent = this.hasEditorTarget ? this.editorTarget.value : ""
        this.modifiedAt = payload.episode.modified_at ? new Date(payload.episode.modified_at) : new Date()
        this.updateStatus(this.modifiedStatus())
      } else {
        this.updateStatus("Save failed")
      }
    } catch (error) {
      console.error("Auto-save error:", error)
      this.updateStatus("Save failed")
    } finally {
      this.isSaving = false
    }
  }

  hasUnsavedChanges() {
    const titleChanged = this.hasTitleTarget ? this.titleTarget.value !== this.lastSavedTitle : false
    const contentChanged = this.hasEditorTarget ? this.editorTarget.value !== this.lastSavedContent : false
    return titleChanged || contentChanged
  }

  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  noteUserInteraction() {
    if (this.hasEverChanged) return

    this.hasEverChanged = true
    this.markTouched()
  }

  async markTouched() {
    if (!this.autoDraftValue || this.touchedPersisted || !this.hasTouchUrlValue) return

    this.touchedPersisted = true
    const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")

    try {
      await fetch(this.touchUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
    } catch (_error) {
      this.touchedPersisted = false
    }
  }

  discardUntouchedDraft() {
    if (!this.autoDraftValue || !this.hasDiscardUrlValue || this.hasEverChanged || this.hasUnsavedChanges()) return

    const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
    if (!token || !navigator.sendBeacon) return

    const formData = new FormData()
    formData.append("authenticity_token", token)
    navigator.sendBeacon(this.discardUrlValue, formData)
  }

  modifiedStatus() {
    if (!this.modifiedAt || Number.isNaN(this.modifiedAt.getTime())) return ""

    const seconds = Math.max(1, Math.round((Date.now() - this.modifiedAt.getTime()) / 1000))
    if (seconds < 60) return "Modified just now"

    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `Modified ${minutes} min ago`

    const hours = Math.round(minutes / 60)
    if (hours < 24) return `Modified ${hours} hr ago`

    const days = Math.round(hours / 24)
    return `Modified ${days} day${days === 1 ? "" : "s"} ago`
  }
}
